import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import api, { sessions as sessionsApi } from '../utils/api';
import { io } from 'socket.io-client';

// Sons par défaut encodés en base64 (tons simples générés en JS via Web Audio)
function playSound(url, type = 'hint') {
  try {
    if (url) {
      const audio = new Audio(url);
      audio.play().catch(() => {});
      return;
    }
    // Son généré via Web Audio API
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    if (type === 'hint') {
      // Ding doux : deux notes
      [0, 0.15].forEach((delay, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.frequency.value = i === 0 ? 880 : 1047;
        osc.type = 'sine';
        gain.gain.setValueAtTime(0.3, ctx.currentTime + delay);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay + 0.4);
        osc.start(ctx.currentTime + delay);
        osc.stop(ctx.currentTime + delay + 0.4);
      });
    } else if (type === 'alarm') {
      // Alarme : ton répété descendant
      [0, 0.2, 0.4, 0.6].forEach((delay) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.frequency.value = 660;
        osc.frequency.linearRampToValueAtTime(330, ctx.currentTime + delay + 0.18);
        osc.type = 'sawtooth';
        gain.gain.setValueAtTime(0.25, ctx.currentTime + delay);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay + 0.19);
        osc.start(ctx.currentTime + delay);
        osc.stop(ctx.currentTime + delay + 0.2);
      });
    }
  } catch {}
}

function fmtTime(sec) {
  if (sec < 0) sec = 0;
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
  return `${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
}

function timerColor(remaining, total) {
  if (total <= 0) return '#00d2b4';
  const pct = remaining / total;
  if (pct > 0.33) return '#00d2b4';
  if (pct > 0.15) return '#f5a623';
  return '#ff4d6d';
}

export default function LivePage() {
  const { roomId }           = useParams();
  const [session, setSession]   = useState(null);
  const [hints, setHints]       = useState([]);
  const [room, setRoom]         = useState(null);
  const [elapsed, setElapsed]   = useState(0);
  const [activeHint, setHintDisplay] = useState(null);
  const [alarm, setAlarm]       = useState(false);
  const [durationOverride, setDurOverride] = useState(null); // mis à jour via WS
  const hintTimeoutRef          = useRef(null);
  const sessionRef              = useRef(null);

  const config = room?.live_config || {};
  const isPortrait = config.orientation === 'portrait';

  const load = useCallback(async () => {
    try {
      const [sessionRes, roomRes] = await Promise.all([
        sessionsApi.current(roomId),
        api.get('/rooms'),
      ]);
      const s = sessionRes.data.session;
      sessionRef.current = s;
      setSession(s);
      setHints(sessionRes.data.hints || []);
      const r = roomRes.data.rooms?.find(r => r.id === roomId);
      setRoom(r);
    } catch {}
  }, [roomId]);

  useEffect(() => { load(); }, [load]);

  // WebSocket
  useEffect(() => {
    const base = process.env.REACT_APP_API_URL?.replace('/api','') || 'http://localhost:4000';
    const socket = io(base, {
      transports: ['websocket', 'polling'], // polling en fallback
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });
    socket.emit('join:room', roomId);

    socket.on('connect', () => {
      socket.emit('join:room', roomId);
      load(); // Recharger la session après reconnexion
    });

    socket.on('session:started', () => { setAlarm(false); load(); });
    socket.on('session:stopped', () => { sessionRef.current = null; setSession(null); setHints([]); setAlarm(false); setDurOverride(null); });

    socket.on('session:hint', (data) => {
      setHints(prev => [...prev, data.hint]);
      showHint(data.hint);
    });

    socket.on('session:adjusted', (data) => {
      if (data.roomId === roomId) setDurOverride(data.durationSec);
    });

    socket.on('session:hint_deleted', (data) => {
      setHints(prev => prev.filter(h => h.id !== data.hintId));
    });

    socket.on('session:alarm', (data) => {
      setAlarm(data.active);
      if (data.active) playSound(config.alarmSound, 'alarm');
    });

    return () => { socket.emit('leave:room', roomId); socket.disconnect(); };
  }, [roomId]);

  // Afficher un indice en bannière
  const showHint = (hint) => {
    playSound(config.hintSound, 'hint');
    setHintDisplay(hint);
    clearTimeout(hintTimeoutRef.current);
    hintTimeoutRef.current = setTimeout(() => setHintDisplay(null), 12000);
  };

  // Timer live — tourne en permanence, lit started_at depuis sessionRef
  useEffect(() => {
    const iv = setInterval(() => {
      const s = sessionRef.current;
      if (!s?.started_at) { setElapsed(0); return; }
      const e = Math.floor((Date.now() - new Date(s.started_at).getTime()) / 1000);
      setElapsed(e);
    }, 500);
    return () => clearInterval(iv);
  }, []); // [] = démarre une fois, tourne toujours

  const durationSec = durationOverride || session?.duration_sec || (room?.duration_min || 60) * 60;
  const remaining   = Math.max(0, durationSec - elapsed);
  const color       = session ? timerColor(remaining, durationSec) : '#333';
  const pct         = session ? Math.min(100, (elapsed / durationSec) * 100) : 0;

  const bgStyle = config.bgImage ? {
    backgroundImage: `url(${config.bgImage})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  } : {};

  if (isPortrait) {
    // ── Mode portrait ────────────────────────────────────────
    return (
      <div style={{ minHeight:'100vh', background:'#0a0a1a', ...bgStyle,
        display:'flex', flexDirection:'column', alignItems:'center',
        justifyContent:'space-between', padding:'40px 20px', fontFamily:'monospace' }}>

        {config.bgImage && <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:0 }}/>}

        <div style={{ position:'relative', zIndex:1, width:'100%', textAlign:'center' }}>
          <div style={{ fontSize:14, color:'#555', letterSpacing:'0.2em', textTransform:'uppercase', marginBottom:8 }}>
            {room?.name}
          </div>
          <div style={{ height:6, background:'rgba(255,255,255,0.08)', borderRadius:3, overflow:'hidden', marginBottom:20 }}>
            <div style={{ height:'100%', width:`${pct}%`, background:color, borderRadius:3, transition:'width 1s linear' }}/>
          </div>
          <div style={{ fontSize:'clamp(72px,25vw,140px)', fontWeight:700, color, lineHeight:1,
            textShadow: session ? `0 0 40px ${color}77` : 'none' }}>
            {fmtTime(remaining)}
          </div>
          {!session && <div style={{ fontSize:14, color:'#444', marginTop:16, letterSpacing:'0.15em' }}>EN ATTENTE</div>}
        </div>

        {/* Indice en bas */}
        {activeHint && (
          <div style={{ position:'relative', zIndex:1, width:'100%',
            background:'rgba(124,92,252,0.92)', borderRadius:16, padding:'20px 24px',
            fontSize:18, fontWeight:700, color:'#fff', textAlign:'center',
            fontFamily:'sans-serif', boxShadow:'0 8px 40px rgba(124,92,252,0.4)',
            animation:'fadeIn 0.3s ease' }}>
            💡 {activeHint.content}
          </div>
        )}
        {!activeHint && <div style={{ height:80 }}/>}

        {alarm && <AlarmOverlay/>}
        <LiveStyles/>
      </div>
    );
  }

  // ── Mode paysage (défaut) ────────────────────────────────────
  return (
    <div style={{ minHeight:'100vh', background:'#0a0a1a', ...bgStyle,
      display:'flex', flexDirection:'column', alignItems:'center',
      justifyContent:'center', fontFamily:'monospace', overflow:'hidden' }}>

      {config.bgImage && <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:0 }}/>}

      <div style={{ position:'relative', zIndex:1, textAlign:'center', width:'100%' }}>
        <div style={{ fontSize:16, color:'#555', letterSpacing:'0.2em', textTransform:'uppercase', marginBottom:16 }}>
          {room?.name}
        </div>

        <div style={{ width:'min(500px,70vw)', margin:'0 auto 20px',
          height:8, background:'rgba(255,255,255,0.08)', borderRadius:4, overflow:'hidden' }}>
          <div style={{ height:'100%', width:`${pct}%`, background:color,
            borderRadius:4, transition:'width 1s linear', boxShadow:`0 0 12px ${color}` }}/>
        </div>

        <div style={{ fontSize:'clamp(80px,18vw,180px)', fontWeight:700, color, lineHeight:1,
          textShadow: session ? `0 0 60px ${color}88, 0 0 120px ${color}33` : 'none',
          transition:'color 1s, text-shadow 1s' }}>
          {fmtTime(remaining)}
        </div>

        {!session && (
          <div style={{ fontSize:16, color:'#444', marginTop:20, letterSpacing:'0.15em' }}>
            EN ATTENTE DE SESSION
          </div>
        )}
      </div>

      {/* Indice actif */}
      {activeHint && (
        <div style={{ position:'fixed', bottom:50, left:'50%', transform:'translateX(-50%)',
          background:'rgba(124,92,252,0.95)', color:'#fff', padding:'18px 36px',
          borderRadius:16, fontSize:20, fontWeight:700, maxWidth:'80vw', textAlign:'center',
          fontFamily:'sans-serif', boxShadow:'0 8px 40px rgba(124,92,252,0.5)',
          zIndex:10, animation:'fadeInUp 0.3s ease', whiteSpace:'pre-wrap' }}>
          💡 {activeHint.content}
        </div>
      )}

      {alarm && <AlarmOverlay/>}
      <LiveStyles/>
    </div>
  );
}

// ── Overlay Hors-Jeu ─────────────────────────────────────────
function AlarmOverlay() {
  return (
    <div style={{ position:'fixed', inset:0, zIndex:100,
      background:'rgba(180,0,0,0.88)', display:'flex', flexDirection:'column',
      alignItems:'center', justifyContent:'center', animation:'pulse 1.5s ease-in-out infinite' }}>
      <div style={{ fontSize:80, marginBottom:16 }}>⚠️</div>
      <div style={{ fontSize:'clamp(40px,10vw,80px)', fontWeight:900, color:'#fff',
        letterSpacing:'0.1em', textAlign:'center', fontFamily:'sans-serif',
        textShadow:'0 2px 20px rgba(0,0,0,0.5)' }}>
        STOP
      </div>
      <div style={{ fontSize:'clamp(18px,4vw,32px)', color:'rgba(255,255,255,0.85)',
        marginTop:12, fontFamily:'sans-serif', letterSpacing:'0.08em' }}>
        ARRÊTEZ-VOUS
      </div>
    </div>
  );
}

function LiveStyles() {
  return (
    <style>{`
      @keyframes fadeInUp {
        from { opacity:0; transform:translateX(-50%) translateY(16px); }
        to   { opacity:1; transform:translateX(-50%) translateY(0); }
      }
      @keyframes fadeIn {
        from { opacity:0; transform:translateY(10px); }
        to   { opacity:1; transform:translateY(0); }
      }
      @keyframes pulse {
        0%,100% { opacity:1; }
        50%      { opacity:0.92; }
      }
      body { margin:0; overflow:hidden; }
    `}</style>
  );
}
